import { useState } from 'react';
import { Link } from 'react-router';
import { useChannels, useCreateChannel } from '../api/queries.js';
import { Badge, Button, Card, Dropdown, Field, Loading, Page, Problem, Table } from '../components/ui.jsx';
import { formatNumber, formatRelative } from '../utils/format.js';

/**
 * Channels are created here and nowhere else: an application cannot bring one into existence by
 * publishing to a name nobody agreed on. That is what keeps the list below a design, not a log of
 * typos.
 */
export function Channels() {
  const channels = useChannels();
  const create = useCreateChannel();

  const [name, setName] = useState('');
  const [kind, setKind] = useState('event');
  const [description, setDescription] = useState('');

  if (channels.isLoading) return <Loading what="i canali" />;
  if (channels.error) return <Problem error={channels.error} />;

  return (
    <Page eyebrow="CONFIGURA" title="Channels" description="Gli indirizzi su cui le applicazioni pubblicano e da cui consumano.">
      <Card title="Nuovo canale">
        <form
          className="form-row"
          onSubmit={(event) => {
            event.preventDefault();
            void create.mutateAsync({ name, kind, description }).then(() => {
              setName('');
              setDescription('');
            });
          }}
        >
          <Field label="Nome" hint="minuscolo e puntato, es. customer.created">
            <input value={name} onChange={(event) => setName(event.target.value)} placeholder="customer.created" required />
          </Field>
          <Field label="Tipo" hint="event: un fatto · command: una richiesta · query: attende risposta" plain>
            <Dropdown
              label="Tipo di canale"
              value={kind}
              onChange={setKind}
              options={[
                { value: 'event', label: 'event' },
                { value: 'command', label: 'command' },
                { value: 'query', label: 'query' },
              ]}
            />
          </Field>
          <Field label="Descrizione">
            <input value={description} onChange={(event) => setDescription(event.target.value)} placeholder="Un cliente è stato creato" />
          </Field>
          <Button type="submit" variant="primary" disabled={create.isPending || !name}>
            {create.isPending ? 'Creo…' : 'Crea'}
          </Button>
        </form>
        {create.error ? <Problem error={create.error} /> : null}
      </Card>

      <Card title={`Canali (${channels.data!.channels.length})`}>
        <Table
          head={
            <tr>
              <th>Canale</th>
              <th>Tipo</th>
              <th>Provider</th>
              <th>Produttori</th>
              <th>Consumatori</th>
              <th className="right">Messaggi</th>
              <th className="right">Dead letter</th>
              <th>Ultimo</th>
            </tr>
          }
          empty="Nessun canale ancora. Il primo di solito è customer.created."
        >
          {channels.data!.channels.map((channel) => (
            <tr key={channel.id} className={channel.archived ? 'row-muted' : undefined}>
              <td>
                <Link to={`/messages?channel=${encodeURIComponent(channel.name)}`}>
                  <strong>{channel.name}</strong>
                </Link>
                {channel.archived ? <Badge tone="neutral">archiviato</Badge> : null}
                {channel.settings.description ? (
                  <>
                    <br />
                    <span className="muted small">{channel.settings.description}</span>
                  </>
                ) : null}
              </td>
              <td>
                <Badge tone="info">{channel.settings.kind}</Badge>
              </td>
              <td className="muted small">{channel.settings.provider}</td>
              <td className="muted small">{channel.producers.join(', ') || '—'}</td>
              <td className="muted small">{channel.consumers.join(', ') || '—'}</td>
              <td className="right">{formatNumber(channel.messageCount)}</td>
              <td className="right">
                {channel.deadLetterCount > 0 ? <Badge tone="bad">{formatNumber(channel.deadLetterCount)}</Badge> : '0'}
              </td>
              <td className="muted small">{formatRelative(channel.lastMessageAt)}</td>
            </tr>
          ))}
        </Table>
      </Card>
    </Page>
  );
}
